import { useEffect, useMemo, useState } from 'react';
import { round } from 'lodash';

import { useAppSelector } from 'utils/hooks';
import { createDragObservable } from 'utils/drag_observable';
import { selectZoomedPPI } from 'selectors/ppi';

import canvases from 'domains/canvases';
import Arrangement from 'domains/arrangement';

import type { Point } from 'types/common_types';

export function useCalculatedBounds(canvasId: string, nodeUid: string) {
  const ppi = useAppSelector(selectZoomedPPI);
  const bounds = useAppSelector((state) =>
    canvases.selectors.calculatedBoundsForCanvasNode(state, { canvasId, nodeUid })
  );

  const withPPI = useMemo(() => bounds.map((b) => round(b * ppi, 6)), [bounds, ppi]);

  return withPPI;
}

export function useArrangement() {
  const withProducts = useAppSelector(canvases.selectors.allWithProducts);

  const size = useMemo(() => {
    const arrangement = Arrangement.createWithProducts(withProducts);

    return {
      height: arrangement.height,
      width: arrangement.width,
    };
  }, [withProducts]);

  return size;
}

export function useDragMovement(deps: {
  /** Selector string for the element we want to track drags, must be constant */
  selector: string;
  /** Selector strings for elements within the tracking element that should not trigger the drag, must be constant */
  exclude: string[];
  /** Should we disable the drag tracking */
  disable: boolean;
  /** Handler for when the drag has completed */
  onComplete: (point: Point) => void;
}) {
  const { selector, exclude, disable, onComplete } = deps;

  const [x, setX] = useState(0);
  const [y, setY] = useState(0);
  const [complete, setComplete] = useState(false);

  useEffect(() => {
    if (complete) {
      onComplete({ x, y });
      setX(0);
      setY(0);
      setComplete(false);
    }
  }, [x, y, complete, onComplete]);

  useEffect(() => {
    if (selector && !disable) {
      const drag = createDragObservable(selector, exclude, () => setComplete(true));
      const sub = drag?.subscribe(({ x, y }) => {
        setX(x);
        setY(y);
      });

      return () => sub?.unsubscribe();
    }
  }, [disable]);

  return { x, y };
}
